import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';  // Importing ClipLoader from react-spinners
import WalletPayButton from './WalletPayButton';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const api=process.env.REACT_APP_API_URL;

const CheckoutForm = ({data, jobDescriptionFile, jobDescriptionText, resumeFile }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [loading, setLoading] = useState(false); // Loading state
  const [price, setPrice] = useState(null);      // cents, from the backend
  const navigate = useNavigate();
  const recovering = useRef(false);

  // Price comes from the backend so the wallet sheet can never display a stale
  // amount after an ONE_TIME_PAYMENT_PRICE change (which would make the wallet's
  // drift guard abort every payment).
  useEffect(() => {
    let alive = true;
    fetch(`${api}payment-config/`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => { if (alive && cfg && cfg.standard) setPrice(cfg.standard); })
      .catch(() => { /* fall back below */ });
    return () => { alive = false; };
  }, []);

  // Warm-up: create the intent as soon as the card form appears, so the
  // backend's generation head start runs while the user types card details.
  // Fire-and-forget and deliberately does NOT touch localStorage, the real
  // createIntent() on submit reuses the same intent server-side.
  useEffect(() => {
    const token = Cookies.get('access_token');
    const session = localStorage.getItem('sessionId');
    if (!token || !session) return;
    fetch(`${api}create-payment-intent/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Session-Id': session,
      },
      body: JSON.stringify({ session_id: session }),
      credentials: 'include',
    }).catch(() => { /* speculative, submit-time createIntent still works */ });
  }, []);

  // Creates the PaymentIntent on the backend. Shared by the card form and the
  // Apple/Google Pay wallet button. Throws with a user-readable message.
  const createIntent = async () => {
    const token = Cookies.get('access_token');
    const session = localStorage.getItem('sessionId');
    if (!session) {
      throw new Error('No active resume session. Please re-upload your resume.');
    }

    let response;
    try {
      response = await fetch(`${api}create-payment-intent/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Session-Id': session,
        },
        body: JSON.stringify({ session_id: session }),
        credentials: 'include',
      });
    } catch (err) {
      throw new Error('Network error reaching payment service. Please try again.');
    }

    if (!response.ok) {
      let detail = `Payment setup failed (HTTP ${response.status})`;
      try {
        const errBody = await response.json();
        detail = errBody.message || errBody.error || detail;
      } catch (_) {}
      throw new Error(detail);
    }

    const body = await response.json();
    const { clientSecret, id, amount, already_paid: alreadyPaid } = body;

    // Session already paid (2nd tab, back-button, retry): the backend hands back
    // the original intent instead of creating a new one. Unlock, never re-charge.
    if (alreadyPaid) {
      if (id) localStorage.setItem('intent_id', id);
      return { id, amount, already_paid: true };
    }

    if (!clientSecret) {
      throw new Error('Payment service did not return a client secret. Please try again.');
    }
    localStorage.setItem('intent_id', id);
    return { clientSecret, id, amount };
  };

  // Post-payment: record status server-side, unlock the funnel, move to the report.
  // Shared by the card path, the wallet path, and the on-load recovery below.
  // Retries transient failures, a charged card that fails to unlock is the worst
  // outcome in this whole flow, so it is worth several attempts before giving up.
  const finalizePayment = useCallback(async (intentId, { attempts = 3 } = {}) => {
    const session = localStorage.getItem('sessionId');
    let lastErr;

    for (let i = 0; i < attempts; i++) {
      try {
        const accessToken = Cookies.get('access_token');
        const response = await fetch(`${api}payment-status/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'X-Session-Id': session,
          },
          body: JSON.stringify({ intent_id: intentId, session_id: session }),
          credentials: 'include',
        });
        if (!response.ok) throw new Error(`Failed to update payment status (HTTP ${response.status})`);
        const body = await response.json();

        // A 200 only means we reached the backend, it reports the REAL Stripe
        // status. Anything other than 'succeeded' (declined, abandoned, still
        // processing) is NOT paid; treating a 200 as success would falsely unlock
        // the funnel for a card that was never charged.
        if (body.status !== 'succeeded') {
          const e = new Error('not_succeeded');
          e.paymentStatus = body.status;
          throw e;
        }

        setPaymentSuccess(true);
        toast.success('Payment confirmed!');

        const completedSteps = JSON.parse(localStorage.getItem('completedSteps')) || [];
        if (!completedSteps.includes('pay')) {
          completedSteps.push('pay');
          localStorage.setItem('completedSteps', JSON.stringify(completedSteps));
        }
        // The payment is fully settled, clear the recovery breadcrumb.
        localStorage.removeItem('intent_id');

        setTimeout(() => {
          setLoading(false);
          navigate('/fullScore', {
            state: { data, jobDescriptionFile, jobDescriptionText, resumeFile },
          });
        }, 1500);
        return;
      } catch (err) {
        lastErr = err;
        if (i < attempts - 1) await new Promise((r) => setTimeout(r, 1000 * (i + 1)));
      }
    }
    throw lastErr || new Error('Failed to update payment status');
  }, [data, jobDescriptionFile, jobDescriptionText, resumeFile, navigate]);

  // RECOVERY: if a previous attempt charged the card but never managed to record
  // the status, `intent_id` is still in localStorage. Re-confirm it on load so the
  // user is unlocked automatically instead of being told to pay again. Only a
  // 'succeeded' status unlocks; a dead intent (declined/abandoned) is cleared so
  // it can't cause a false recovery, while a still-processing one is kept.
  useEffect(() => {
    const pending = localStorage.getItem('intent_id');
    const session = localStorage.getItem('sessionId');
    if (!pending || !session || recovering.current) return;
    recovering.current = true;

    setLoading(true);
    finalizePayment(pending, { attempts: 2 })
      .catch((err) => {
        const st = err && err.paymentStatus;
        // Terminal non-paid statuses will never become 'succeeded', drop the
        // breadcrumb so we don't keep "recovering" a payment that never happened.
        if (st === 'requires_payment_method' || st === 'canceled') {
          localStorage.removeItem('intent_id');
        }
        setLoading(false);
      });
  }, [finalizePayment]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || loading) {
      return;
    }

    setLoading(true); // Start loading state

    let intent;
    try {
      intent = await createIntent();
    } catch (err) {
      toast.error(err.message);
      setErrorMessage(err.message);
      setLoading(false);
      return;
    }

    // Already paid, skip straight to unlocking, do not charge again.
    if (intent.already_paid) {
      try {
        await finalizePayment(intent.id);
      } catch (err) {
        toast.error('You have already paid. Please refresh the page to continue.');
        setLoading(false);
      }
      return;
    }

    const { error, paymentIntent } = await stripe.confirmCardPayment(intent.clientSecret, {
      payment_method: { card: elements.getElement(CardElement) },
    });

    if (error) {
      setErrorMessage(error.message);
      toast.error(error.message);
      setLoading(false); // Stop loading state
    } else if (paymentIntent.status === 'succeeded') {
      // The card HAS been charged. Show success immediately, so a later failure to
      // record the status can never read as "payment failed" and invite a re-pay.
      setPaymentSuccess(true);
      try {
        await finalizePayment(intent.id);
      } catch (err) {
        toast.error('Payment succeeded. We hit a problem unlocking your report, reload this page and it will resume automatically. Do NOT pay again.');
        setLoading(false); // Stop loading state
      }
    }
  };

  // Wallet (Apple/Google Pay) post-payment hook, same finalize as the card path.
  const handleWalletPaid = async (intentId) => {
    setLoading(true);
    setPaymentSuccess(true); // the card is already charged at this point
    try {
      await finalizePayment(intentId);
    } catch (err) {
      setLoading(false);
      throw err; // WalletPayButton shows the "reload, do NOT pay again" message
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        {/* Apple Pay / Google Pay. Hidden while the card path is charging or once
            paid, a live wallet button beside a spinning card button is how a user
            ends up charged twice. Amount comes from the backend, never hardcoded. */}
        <div className="mt-8">
          {/* Render the wallet only once the real price is loaded, the sheet's
              amount is immutable, so a fallback could show the wrong total. */}
          {price != null && (
            <WalletPayButton
              amount={price}
              label="Resume Advisor, Full Report"
              createIntent={createIntent}
              onPaid={handleWalletPaid}
              disabled={loading || paymentSuccess || !stripe}
            />
          )}
        </div>
        <CardElement className="mt-8" />
        <div className="w-full flex justify-center items-center">
          <button
            className="btn bg-black text-white hover:bg-red-600 hover:text-orange font-bold py-2 px-4 rounded mt-8 transition duration-300 ease-in-out"
            type="submit"
            disabled={!stripe || loading} // Disable if loading
          >
            Pay Here
          </button>
        </div>

        {errorMessage && <div className="text-red-500 mt-2">{errorMessage}</div>}
        {paymentSuccess && <div className="text-green-500 mt-2">Payment Successful!</div>}

        {/* Show loader when loading state is true */}

        <div className='w-full flex justify-center items-center'>
        {loading && (
          <div className="loader mt-4">
            <ClipLoader color="#2c5141" loading={loading} size={50} />
          </div>
        )}
        </div>
      </form>
    </>
  );
};

const Payment = ({ setPaymentSuccess, data, jobDescriptionFile, jobDescriptionText, resumeFile }) => (
  <Elements stripe={stripePromise}>
    <CheckoutForm 
      setPaymentSuccess={setPaymentSuccess} 
      data={data} 
      jobDescriptionFile={jobDescriptionFile} 
      jobDescriptionText={jobDescriptionText} 
      resumeFile={resumeFile} 
    />
  </Elements>
);

export default Payment;
